import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	RoleCreateModal,
	RoleListTable,
	RoleRemoveModal,
	RoleEditCloneModal,
	UiPagination,
	ToolPanel,
	IconPlus,
	IconDeleteBin,
	ESortOrder
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useRoleList, useRoleFiltersPage, useRoleSortPage } from '../hooks'
import * as lib from '../lib'
import * as state from '../states'
import { ERoleAccess, IFetchItemsSetup, IRole, ETableAction, ETableRemoveType } from '../types'
import { TAKE, MENU } from '../constants'
import { Access } from '../lib'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

export const TopButtonsBox = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
`

export const RolePage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)

	const setFetchIsCommonToReload = useSetAtom(state.fetchIsCommonToReloadAtom)
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const pageTitle = t(MENU.role.title)

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		roleFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useRoleFiltersPage()

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useRoleSortPage()

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useRoleList(setup, send)


	const [createRole, setCreateRole] = useState<boolean>(false)
	const [editRole, setEditRole] = useState<IRole | null>(null)
	const [cloneRole, setCloneRole] = useState<IRole | null>(null)
	const [removeRole, setRemoveRole] = useState<IRole | null>(null)
	const [removeRoles, setRemoveRoles] = useState<IRole[] | null>(null)
	const [selectedRows, setSelectedRows] = useState<IRole[]>([])

	const onCreate = (e: React.MouseEvent) => {
		e.preventDefault()
		setCreateRole(true)
	}
	const onEdit = (item: IRole, e: React.MouseEvent) => {
		e.preventDefault()
		setEditRole(item)
	}
	const onClone = (item: IRole, e: React.MouseEvent) => {
		e.preventDefault()
		setCloneRole(item)
	}
	const onRemove = (item: IRole, e: React.MouseEvent) => {
		e.preventDefault()
		setRemoveRole(item)
	}

	const onMultiSelect = (selectedRows: IRole[]) => {
		setSelectedRows(selectedRows)
	}

	const onMultiDelete = async (e: React.MouseEvent) => {
		e.preventDefault()
		//console.log(`onMultiDelete: selectedRows:: ${selectedRows}`)
		setRemoveRoles(selectedRows)
	}

	return (
		<>
			<PageTitle title={pageTitle}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					{/* remove CREATE button for 'account' main page (without pre-selected group) */}
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						<TopButtonsBox>
							{
								!!onMultiDelete && !!$access.$(ERoleAccess.RoleRemove) && selectedRows.length > 0 ? (
									(
										<button
											type="button"
											className="btn btn-primary btn-danger d-flex align-items-center"
											onClick={onMultiDelete}
										>
											<IconDeleteBin size={16}/>
											<span className="d-none d-lg-block">
												{`${t(ALL_LANG_KEY.role_page.btn_delete_multiple_items, { cnt: selectedRows.length })}`}
											</span>
										</button>
									)
								) : null
							}

							{
								!!onCreate && !!$access.$(ERoleAccess.RoleCreate)
									? (
										<button
											type="button"
											className="btn btn-primary d-flex align-items-center"
											onClick={onCreate}
										>
											<IconPlus/>
											<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.role_page.btn_add_new)}`}</span>
										</button>
									)
									: null
							}
						</TopButtonsBox>

					</div>

					<Filters
						config={roleFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RolePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<RoleListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onEdit={onEdit}
						onClone={onClone}
						onRemove={onRemove}
						onMultiSelect={onMultiSelect}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!createRole && !!$access.$(ERoleAccess.RoleCreate) && <RoleCreateModal
					show={createRole}
					onClose={() => setCreateRole(false)}
					onSave={() => {
						setCreateRole(false)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
						setFetchIsCommonToReload(state.launchUpdateListAccountTeam)
					}}
				/>
			}

			{
				!!editRole && !!$access.$(ERoleAccess.RoleModify) && <RoleEditCloneModal
					action={ETableAction.edit}
					item={editRole}
					show={!!editRole}
					onClose={() => setEditRole(null)}
					onSave={() => {
						setEditRole(null)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
						setFetchIsCommonToReload(state.launchUpdateListAccountTeam)
					}}
				/>
			}

			{
				!!cloneRole && !!$access.$(ERoleAccess.RoleModify) && <RoleEditCloneModal
					action={ETableAction.clone}
					item={cloneRole}
					show={!!cloneRole}
					onClose={() => setCloneRole(null)}
					onSave={() => {
						setCloneRole(null)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
						setFetchIsCommonToReload(state.launchUpdateListAccountTeam)
					}}
				/>
			}

			{
				!!removeRole && !!$access.$(ERoleAccess.RoleRemove) && <RoleRemoveModal
					type={ETableRemoveType.single}
					item={removeRole}
					show={!!removeRole}
					onClose={() => setRemoveRole(null)}
					onSave={() => {
						setRemoveRole(null)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
						setFetchIsCommonToReload(state.launchUpdateListAccountTeam)
					}}
				/>
			}

			{
				!!removeRoles && !!$access.$(ERoleAccess.RoleRemove) && <RoleRemoveModal
					type={ETableRemoveType.multi}
					items={removeRoles}
					show={!!removeRoles}
					onClose={() => setRemoveRoles(null)}
					onSave={() => {
						setRemoveRoles(null)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
						setFetchIsCommonToReload(state.launchUpdateListAccountTeam)
					}}
				/>
			}

		</>
	)
}
